import aaDataJson from "@/data/one_piece/aa_data.json";
const aaData = aaDataJson as AAData;
import riftboundAaDataJson from "@/data/riftbound/aa_data.json";
const riftboundAaData = riftboundAaDataJson as AAData;
import { CardPrices } from "@/models/tcgplayer-prices";
import { getLatestCardPrices } from "@/lib/api/prices";
import { AACard } from "@/models/alt-arts";
import { SupportedGames } from "@/types/game";
import {
  getCardId as getUACardId,
  getUAToTCGPlayerIdMap,
} from "@/lib/games/unionArena/getCardDataLocal";
import { getCardId as getRBCardId } from "@/lib/games/riftbound/getCardDataLocal";

/**
 * Per-game price cache, filled on first use.
 *
 * Deliberately not built from the game registry: BaseHandler imports this
 * module, so touching GAMES here runs while games/index.ts is still
 * initializing and throws "Cannot access 'GAMES' before initialization".
 * Creating each game's entry on demand keeps it registry-free without going
 * back to a hardcoded list of games.
 */
const CACHED_CARD_PRICES: Partial<Record<SupportedGames, CardPrices>> = {};

function cacheFor(game: SupportedGames): CardPrices {
  return (CACHED_CARD_PRICES[game] ??= {});
}
let CACHED_OPID_MAP: { [key: string]: number } = {};
let CACHED_RBID_MAP: { [key: string]: number } = {};

type SrcType = "asia_en" | "en" | "tcgplayer";

interface AACardData {
  asia_en_id: string | null;
  card_number: string;
  card_sets: string[] | null;
  count: number;
  hide: boolean;
  id: string;
  name: string;
  set: string;
  src: SrcType;
  tags: string[];
  tcgplayer_id: string | null;
}

type AAData = {
  [key: string]: AACardData[];
};

function getOpToIdMap(): { [key: string]: number } {
  if (Object.keys(CACHED_OPID_MAP).length > 0) {
    return CACHED_OPID_MAP;
  }
  for (const [_key, cards] of Object.entries(aaData)) {
    for (const card of cards) {
      if (
        card.src.toLowerCase() === "en" &&
        card.tcgplayer_id &&
        card.tcgplayer_id !== ""
      ) {
        CACHED_OPID_MAP[card.id] = parseInt(card.tcgplayer_id);
      }
    }
  }
  return CACHED_OPID_MAP;
}

function getRbToIdMap(): { [key: string]: number } {
  if (Object.keys(CACHED_RBID_MAP).length > 0) {
    return CACHED_RBID_MAP;
  }
  for (const [_key, cards] of Object.entries(riftboundAaData)) {
    for (const card of cards) {
      if (
        card.src.toLowerCase() === "en" &&
        card.tcgplayer_id &&
        card.tcgplayer_id !== ""
      ) {
        CACHED_RBID_MAP[card.id] = parseInt(card.tcgplayer_id);
      }
    }
  }
  return CACHED_RBID_MAP;
}

export async function getCardPrice2(game: SupportedGames, cardId: string) {
  try {
    if (Object.keys(cacheFor(game)).length === 0) {
      let data: CardPrices | undefined = await getLatestCardPrices(game);
      if (!data) throw new Error("failed to fetch card prices");
      CACHED_CARD_PRICES[game] = data;
    }

    const productId = getTCGPlayerProductId(game as SupportedGames, cardId);
    if (productId && Object.keys(cacheFor(game)).includes(productId)) {
      const priceData = cacheFor(game)[productId];

      // Use low price if available
      if (priceData.l && priceData.l.trim() !== "") {
        const price = parseFloat(priceData.l);
        if (!isNaN(price)) return price;
      }

      // Fallback to market price
      if (priceData.m && priceData.m.trim() !== "") {
        const price = parseFloat(priceData.m);
        if (!isNaN(price)) return price;
      }
    }
    return null;
  } catch (error) {
    // console.error(`Error getting card price for ${opId}:`, error);
    return null;
  }
}

type Mapping = {
  [key: string]: number;
};

export async function getDecklistPrice(
  game: SupportedGames,
  decklist: string,
): Promise<number> {
  let price = 0;
  let cards = decklist.split(";");

  for (const card of cards) {
    const [count, id] = card.split("x");
    try {
      let curPrice = await getCardPrice2(game, id);
      if (curPrice !== null) {
        price = price + parseInt(count) * curPrice;
      }
    } catch {
      continue;
    }
  }
  return price;
}

export function getTCGPlayerProductId(
  game: SupportedGames,
  cardId: string,
): string | undefined {
  if (game === "one-piece") {
    // Check direct mapping for specific card variant
    const map: Mapping = getOpToIdMap();
    if (Object.keys(map).includes(cardId)) {
      return map[cardId].toString();
    }
  } else if (game === "union-arena") {
    const newCardId = getUACardId(cardId);
    const map: Mapping = getUAToTCGPlayerIdMap();
    if (newCardId && Object.keys(map).includes(newCardId)) {
      return map[newCardId].toString();
    }
  } else if (game === "riftbound") {
    const newCardId = getRBCardId(cardId);
    const map: Mapping = getRbToIdMap();
    if (newCardId && Object.keys(map).includes(newCardId)) {
      return map[newCardId].toString();
    }
  } else if (game === "palworld") {
    // Palworld cards have tcgplayer_id directly on the card data
    // This is populated from aa-data fetched from the backend
    const { getGameHandler } = require("@/games");
    const gameHandler = getGameHandler("palworld");
    const cardData = gameHandler.getCardData(cardId);
    return cardData?.tcgplayer_id;
  } else {
    throw new Error(
      `getTCGPlayerProductId: Game "${game}" is not implemented. ` +
        `Please add a case for this game in lib/tcgplayer/getCardPrice.tsx`,
    );
  }
  return undefined;
}

export function reverseMap(obj: Record<any, any>): Record<string, string> {
  const reversedObj: Record<string, string> = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      reversedObj[value.toString()] = key;
    }
  }
  return reversedObj;
}

export function getOpId(tcgplayerId: string) {
  const thisMap: Record<string, number> = getOpToIdMap();
  const curMap: any = reverseMap(thisMap);
  // curMap has number as key?
  return curMap[tcgplayerId];
}

export function getRbId(tcgplayerId: string) {
  const thisMap: Record<string, number> = getRbToIdMap();
  const curMap: any = reverseMap(thisMap);
  return curMap[tcgplayerId];
}

export function getTcgPlayerIdAA(card: AACard): string | undefined {
  if (!card) {
    return undefined;
  }
  if (card.src.toLowerCase() === "en") {
    if (card.tcgplayer_id === null) {
      return undefined;
    } else {
      return card.tcgplayer_id;
    }
  } else if (card.src.toLowerCase() === "tcgplayer") {
    return card.id;
  } else {
    return undefined;
  }
}
